/*
 * Author: Roger
 * File Created: Wednesday, 24-09-04 06:10 pm
 * Last Modified: Friday, 24-11-08 11:46 am, by Roger
 */
import {
  Box,
  Button,
  Stack,
  stackClasses,
  styled,
  Typography,
} from '@mui/material';

import Image from '@/components/Image';

interface ProductStackProps {
  stackbgcolor?: string;
}

export const ProductStack = styled(Stack)<ProductStackProps>(
  ({ theme, stackbgcolor }) => ({
    position: 'relative',
    height: '100%',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '80px',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: stackbgcolor || 'none',
      borderRadius: '24px',
      zIndex: 0,
      [theme.breakpoints.up('sm')]: {
        top: '120px',
      },
      [theme.breakpoints.up('md')]: {
        top: '160px',
      },
      [theme.breakpoints.up('lg')]: {
        top: '204px',
      },
    },
    '& > *': {
      zIndex: 3,
    },
  }),
);

export const ProductCardBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  gap: '16px',
  [theme.breakpoints.only('xs')]: {
    padding: '24px 0',
  },
}));

export const ProductImage = styled(Image)(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxWidth: '582px',
  alignSelf: 'center',
  [theme.breakpoints.only('xs')]: {},
}));

export const ProductButton = styled(Button)(() => ({
  borderRadius: '30px',
  padding: '12px 40px',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '120%',
  cursor: 'default',
  '& .MuiButton-endIcon': {
    marginLeft: '4px',
  },
}));

export const ProductIntroBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
}));

export const ProductTitle = styled(Typography)(({ theme }) => ({
  fontSize: '32px',
  fontWeight: 700,
  lineHeight: 'normal',
  marginBottom: '40px',
  [theme.breakpoints.only('xs')]: {
    fontSize: '24px',
    marginBottom: '24px',
    textAlign: 'center',
  },
  [theme.breakpoints.only('sm')]: {
    fontSize: '26px',
    marginBottom: '28px',
  },
  [theme.breakpoints.only('md')]: {
    fontSize: '28px',
    marginBottom: '32px',
  },
  '& .upscript': {
    verticalAlign: 'super',
    fontSize: '22px',
    [theme.breakpoints.only('xs')]: {
      fontSize: '16px',
    },
    [theme.breakpoints.only('sm')]: {
      fontSize: '18px',
    },
    [theme.breakpoints.only('md')]: {
      fontSize: '20px',
    },
  },
}));

export const ChipBox = styled(Box)<{ vip: string }>(({ vip, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 6px',
  borderRadius: '4px',
  border: '1.154px solid rgba(132, 142, 156, 0.30)',
  background: 'rgba(132, 142, 156, 0.15)',
  color: vip === 'yes' ? '#EAC69F' : '#A993FF',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '16px',
  [theme.breakpoints.only('xs')]: {
    fontSize: '12px',
    lineHeight: '12px',
    padding: '2px 4px',
  },
  [theme.breakpoints.only('sm')]: {
    fontSize: '14px',
    lineHeight: '14px',
  },
  [theme.breakpoints.only('md')]: {
    fontSize: '16px',
    lineHeight: '16px',
  },
}));

export const ProductTextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '20px 24px',
  borderRadius: '12px',
  background: '#212121',
  marginBottom: '16px',
  [`& > .${stackClasses.root}`]: {
    flex: 1,
  },
  [`&:last-of-type`]: {
    marginBottom: 0,
  },
  [theme.breakpoints.only('xs')]: {
    marginBottom: '12px',
  },
  [theme.breakpoints.only('sm')]: {
    marginBottom: '12px',
  },
  [theme.breakpoints.only('md')]: {
    marginBottom: '14px',
  },
}));

export const NumberBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  borderRadius: '13px',
  border: '1px solid #848E9C',
  marginRight: '12px',
  color: '#848E9C',
  fontSize: '16px',
  fontWeight: 500,
  [theme.breakpoints.only('xs')]: {
    fontSize: '12px',
    width: '16px',
    height: '16px',
    borderRadius: '8px',
  },
  [theme.breakpoints.only('sm')]: {
    fontSize: '12px',
    width: '18px',
    height: '18px',
    borderRadius: '9px',
  },
  [theme.breakpoints.only('md')]: {
    fontSize: '14px',
    width: '20px',
    height: '20px',
    borderRadius: '10px',
  },
}));

export const ProductSubTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#D7D7D7',
  fontWeight: 500,
  lineHeight: '24px',
  fontSize: '24px',
  marginBottom: '8px',
  [theme.breakpoints.only('xs')]: {
    fontSize: '16px',
    lineHeight: '16px',
  },
  [theme.breakpoints.only('sm')]: {
    fontSize: '18px',
    lineHeight: '18px',
  },
  [theme.breakpoints.only('md')]: {
    fontSize: '20px',
    lineHeight: '20px',
  },
}));

export const ProductText = styled(Typography)(({ theme }) => ({
  color: '#848E9C',
  fontWeight: 400,
  lineHeight: 'normal',
  fontSize: '16px',
  [theme.breakpoints.only('xs')]: {
    fontSize: '14px',
  },
  [theme.breakpoints.only('sm')]: {
    fontSize: '14px',
  },
  [theme.breakpoints.only('md')]: {
    fontSize: '16px',
  },
}));
